<template>
    <VBtn class="dropdown-btn inputs__dropdown" color="ghost" @click="toggleDropdown" :class="{ hasError: errorMessage }" type="button">
        <span>{{ passengerStore.getPassengersPlaceholder(hasReturn) }}</span>
        <SvgIcon class="icon" icon="carbon/chevron-down" :class="{ open: showDropdown }" />
    </VBtn>

    <div v-if="showDropdown" ref="dropdownTarget" class="dropdown" :class="{ double: differentDetails && hasReturn }">
        <div v-if="!isLoggedIn" class="sign-in">
            <h5>Are you an Islander?</h5>
            <h5>Do you have a NorthLink ID?</h5>
            <a class="sign-in__button" href="/booking/login">
                <VBtn type="button" color="secondary">Sign In</VBtn>
            </a>
        </div>
        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
        <div class="passenger-select" :class="{ return: differentDetails && hasReturn }">
            <div class="quantity-container">
                <h5 v-if="differentDetails && hasReturn">Outward</h5>
                <div class="quantity">
                    <PassengerSelectRow v-for="(option, index) in options" :key="index" :option="option" :direction="Direction.Outward" />
                </div>
            </div>
            <div v-if="differentDetails && hasReturn" class="quantity-container">
                <h5>Return</h5>
                <div class="quantity">
                    <PassengerSelectRow
                        v-for="option in options"
                        class="return-passenger"
                        :key="option.passengerType"
                        :option="option"
                        :direction="Direction.Return"
                    />
                </div>
            </div>
        </div>
        <div class="footer">
            <div class="checkboxes">
                <VCheckbox v-model="concession" id="concessions" label="Access other concessions" />
                <VCheckbox v-if="hasReturn" v-model="differentDetails" id="differentDetails" label="Different details for return" />
            </div>
            <div class="button">
                <VBtn @click="closeSelect" type="button">Done</VBtn>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Direction } from "@/models/Enums";
import { passengerOptions } from "@/modules/Quickform/config/passengerOptions";
import { usePassengerStore } from "@/store/passengerStore";
import { Ref, computed, ref } from "vue";
import PassengerSelectRow from "./PassengerSelectRow.vue";
import { VBtn, VCheckbox } from "v-ferry-components";
import SvgIcon from "@/components/base/SvgIcon.vue";
import { onClickOutside } from "@vueuse/core";
import { sessionKeys } from "@/config/sessionKeys";

interface Props {
    errorMessage?: string;
    next?: Ref<any>;
    hasReturn?: boolean;
}

defineProps<Props>();

const emit = defineEmits(["onPassengerSelect"]);

const showDropdown = ref(false);

const dropdownTarget = ref();

onClickOutside(dropdownTarget, () => (showDropdown.value = false));

const passengerStore = usePassengerStore();

const concession = ref(false);

const differentDetails = computed({
    get(): boolean {
        return passengerStore.getDifferentDetails;
    },
    set(isDifferentDetails: boolean) {
        passengerStore.setDifferentDetails(isDifferentDetails);
    },
});

const options = computed(() => (concession.value ? passengerOptions : passengerOptions.filter((p) => p.displayDefault === true)));

const isLoggedIn = computed(() => {
    var sessionUser = sessionStorage.getItem(sessionKeys.user);
    if (!sessionUser) return;
    const user = JSON.parse(sessionUser);
    return user != null ? !!user.loggedIn : false;
});

const closeSelect = () => {
    emit("onPassengerSelect");
    showDropdown.value = false;
};

const toggleDropdown = () => {
    if (showDropdown.value) {
        closeSelect();
    } else {
        showDropdown.value = true;
    }
};
</script>

<style lang="scss" scoped>
.sign-in {
    @include flex(column, center, center, 10px);
    margin-bottom: 20px;
    h5 {
        margin: 0;
        font-size: 0.875rem;
    }

    &__button {
        text-decoration: none;
        margin-top: 10px;
        width: 100%;
        .v-btn {
            width: 100%;
            text-transform: uppercase;
        }
    }

    a {
        color: var(--c-base-blue);
        &:hover {
            color: var(--c-secondary-blue);
        }
    }

    &__link {
        display: contents;
    }
}

.error-message {
    text-align: center;
    position: relative;
    margin: 5px;
    padding: 15px;
    font-weight: bold;
    font-size: 1rem;
    color: #292929;

    &::before {
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        content: "";
        border-bottom: 1px solid var(--c-light-grey);
    }
}
.return {
    display: flex;
    align-items: flex-end;
    justify-content: space-evenly;

    @include tablet {
        @include flex(column, unset, unset, 30px);
    }
}

.passenger-select {
    .quantity-container {
        flex: 1;
        h5 {
            text-align: center;
            text-transform: uppercase;
            font-size: 0.875rem;
            margin: 0px 10px 10px 0px;
        }
        .quantity {
            overflow-y: auto;
            @include flex(column);
        }
    }
}

.footer {
    @include flex(column, center, center, 20px);
    .checkboxes {
        align-self: flex-start;
        @include flex(column, flex-start, flex-start, 20px);
    }
    padding-top: 0;
    .button {
        width: 100%;
        max-width: 300px;
        button {
            width: 100%;
            border-radius: 5px;
            padding: 15px;
            background-color: white;
            color: var(--c-base-blue);
            border: var(--c-base-blue) 1px solid;
            text-transform: uppercase;

            &:hover {
                background-color: #f8f8f8;
            }
        }
    }
}
</style>
